import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import * as backend from "@/auth/backend";
import {
  DEFAULT_ROLE_ID,
  SessionContext,
  readStoredSession,
  writeStoredSession,
  type SessionValue,
} from "@/auth/session";
import {
  forgetCacheAfterSignOut,
  resetCacheForNewSession,
  stopQueriesBeforeSignOut,
} from "@/auth/session-cache";
import { useDataSource } from "@/adapters/data-source";
import { LIVE } from "@/lib/live";
import { clearBrowserState, releaseThisDevice } from "@/lib/pwa/device-state";

/**
 * What `activeRoleId` is against the API, where there is no demo persona. Not
 * a role id any role has, so nothing resolves it to a role — which is the
 * point: a stand-in id such as the fixtures' would never match a real role
 * (#429).
 */
const NO_PERSONA = "";

/**
 * Backs `useSession()`.
 *
 * State is a `useState` seeded once from `sessionStorage`, not re-read on
 * every render — unlike `useTheme`, nothing else can change this out from
 * under the tab (`sessionStorage` is not shared across tabs the way
 * `localStorage` is), so there is no external store to subscribe to.
 *
 * The persona is the demo's: the record in `sessionStorage` and the fixture
 * role it names exist only on fixtures. A build with an API neither reads nor
 * writes it, and the code that does is not in that bundle (`lib/live.ts`,
 * #429); who is signed in comes from `/api/me`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const data = useDataSource();
  const [activeRoleId, setActiveRoleIdState] = useState<string>(() =>
    LIVE ? NO_PERSONA : (readStoredSession()?.activeRoleId ?? DEFAULT_ROLE_ID),
  );

  const value: SessionValue = {
    activeRoleId,
    signIn: async (email, password) => {
      await backend.signIn(email, password);
      /*
        Everything cached belongs to whoever was signed in before — nothing,
        usually, but a second person signing in on one browser must not see
        the first one's orders sitting in the cache. Waited for, so the shell is
        drawn with this person's permissions and not the signed-out fallback.
        Reset, not cleared: see `resetCacheForNewSession` for why that matters.
      */
      await resetCacheForNewSession(queryClient);
    },
    signOut: async () => {
      // Before the session ends, not after: nothing may be in flight to come
      // back 401 (#523).
      await stopQueriesBeforeSignOut(queryClient);
      // While the session still exists: this browser stops receiving this
      // person's push notifications (#446) — see `lib/pwa/device-state.ts`.
      await releaseThisDevice(data.notifications);
      await backend.signOut();
      if (!LIVE) setActiveRoleIdState(DEFAULT_ROLE_ID);
      // Forgotten, not refetched. The screen is still mounted for the moment
      // before the caller navigates away, and a refetch now is a burst of 401s
      // for answers nobody will read — see `forgetCacheAfterSignOut`.
      forgetCacheAfterSignOut(queryClient);
      // Then what the browser itself kept: caches and stored choices.
      await clearBrowserState();
    },
    setActiveRoleId: (roleId) => {
      // Only `PersonaSwitcher` calls this, and it is not mounted against the API.
      if (LIVE) return;
      writeStoredSession({ activeRoleId: roleId });
      setActiveRoleIdState(roleId);
    },
  };

  return <SessionContext value={value}>{children}</SessionContext>;
}
